import React, { Suspense, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import TopNavHorizontal from "../components/TopNavHorizontal";
import useWidth from "../hooks/useWidth";
import useMenuLayout from "../hooks/useMenuLayout";
import useMenuHidden from "../hooks/useMenuHidden";
import { motion, AnimatePresence } from "framer-motion";
import PageErrorBoundary from "../components/PageErrorBoundary";
import MobileMenu from "../components/MobileMenu";
import { useSelector } from "react-redux";
import { selectUser } from "../slices/userReducer";
import { useLocation, useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import ImpersonationBanner from "../components/ImpersonationBanner";

export default function AuthorizedLayout({ language, setLanguage, children, publicAccess = false }) {
  const { width, breakpoints } = useWidth();
  // content width
  const [menuType] = useMenuLayout();
  const [menuHidden] = useMenuHidden();

  const navigate = useNavigate();

  const user = useSelector(selectUser);

  const location = useLocation();

  // Homepage goes full-bleed (no left sidebar rail) — matches the new
  // dashboard hero design; all other pages keep the sidebar.
  const isHomepage = location.pathname === "/";
  const homepageFullBleed = isHomepage && width > breakpoints.xl;

  // Back-office pages keep the left sidebar; everything else uses the
  // horizontal top nav (mock design).
  const isBackOffice =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/partner-dashboard") ||
    location.pathname.startsWith("/families") ||
    location.pathname.startsWith("/team-member") ||
    location.pathname.startsWith("/profile/ugc");

  const switchHeaderClass = () => {
    if (
      menuType === "horizontal" ||
      menuHidden ||
      homepageFullBleed ||
      !isBackOffice
    ) {
      return "ml-0";
    } else {
      return "ml-[250px]";
    }
  };

  useEffect(() => {
    if (!publicAccess && !user.loading && !user.token && !user.promoterRef && !user.id) {
      // If the user has not created an account yet, then we send them to the create account form
      const returnPath = location.pathname + location.search;
      navigate(`/login?redirectUrl=${encodeURIComponent(returnPath)}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, publicAccess]);

  const hasProtectedAccess = !!(user.token || user.promoterRef || user.id);

  // Never mount protected page children before auth has resolved. Besides
  // avoiding a flash of restricted UI, this prevents their data hooks from
  // issuing requests while an anonymous visitor is being redirected.
  if (!publicAccess && (user.loading || !hasProtectedAccess)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0711]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#7721E5] border-t-transparent" />
      </div>
    );
  }

  // mobile menu
  // Homepage is full-bleed (its hero carries the nav inside HomeTopBanner).
  return (
    <>
      <ImpersonationBanner />
      {/* TODO: add mobile header with back,logo and language  */}
      {/* <Header className={width > breakpoints.xl ? switchHeaderClass() : ''} /> */}
      {isBackOffice &&
        menuType === "vertical" && width > breakpoints.xl && !menuHidden && (
        <Sidebar language={language} setLanguage={setLanguage} />
      )}
      {!isBackOffice && width > breakpoints.xl && (
        <div className="fixed inset-x-0 z-[900]" style={{ top: "var(--app-banner-height, 0px)" }}>
          <TopNavHorizontal setLanguage={setLanguage} />
        </div>
      )}
      {width < breakpoints.lg &&
        (location.pathname === "/" ||
          location.pathname === "/events" ||
          location.pathname === "/lifestyle" ||
          location.pathname === "/deal-requests" ||
          location.pathname === "/spin-and-win" ||
          location.pathname === "/housemates" ||
          location.pathname === "/inbox" ||
          location.pathname === "/team-member" ||
          location.pathname.includes("/profile")) && (
          <MobileMenu/>
        )}
      <div
        className={`content-wrapper transition-all duration-150 ${
          width > 1280 ? switchHeaderClass() : ""
        }`}
        style={
          !isBackOffice && width > breakpoints.xl && !isHomepage
            ? { paddingTop: "calc(var(--app-banner-height, 0px) + 64px)" }
            : undefined
        }
      >
        {/* md:min-h-screen will h-full*/}
        <div className="page-content page-min-height">
          <div className={"w-full"}>
            {/* TODO: ADD LOADING ANIMATION */}
            <PageErrorBoundary routeKey={location.pathname}>
            <Suspense fallback={
              <div className="flex justify-center items-center min-h-[200px]">
                <div className="w-[32px] h-[32px] border-2 border-[#7721E5] border-t-transparent rounded-full animate-spin" />
              </div>
            }>
              <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className=""
              >
                {children}
              </motion.div>
              </AnimatePresence>
            </Suspense>
            </PageErrorBoundary>
          </div>
        </div>
        {/* Hide footer on checkout and conversation detail pages */}
        {!location.pathname.includes("/checkout") &&
          !/^\/inbox\/[^/]+/.test(location.pathname) && (
          <Footer language={language} setLanguage={setLanguage} />
        )}
      </div>
    </>
  );
}
